/**
 * AtlasIdentityModule — the Atlas Passport.
 *
 * Owns the identity kiosk overlay. On connect, resolves an existing Account for
 * the caller identity (durable passport) or prompts for username + display name
 * and calls `createAccount`. Emits `atlas:account-ready` once the passport
 * exists so the world/avatar layers can spawn the player.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import type { DbConnection } from '../module_bindings/index.js';

export class AtlasIdentityModule implements IModule {
  readonly name = 'AtlasIdentity';

  private overlay!: HTMLElement;
  private usernameInput!: HTMLInputElement;
  private displayInput!: HTMLInputElement;
  private submitBtn!: HTMLButtonElement;
  private errEl!: HTMLElement;
  private proceeded = false;
  /** True once the account.onInsert watcher has been registered (one-time, in init). */
  private insertWatcherBound = false;

  init(_app: App): void {
    this.overlay = document.getElementById('login')!;
    this.usernameInput = document.getElementById('login-username') as HTMLInputElement;
    this.displayInput = document.getElementById('login-display') as HTMLInputElement;
    this.submitBtn = document.getElementById('login-submit') as HTMLButtonElement;
    this.errEl = document.getElementById('login-err')!;

    this.submitBtn.addEventListener('click', () => this.submit());
    this.displayInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.submit();
    });
  }

  /**
   * Called by the network module once connected. Resolves the passport: if an
   * Account row already exists for this identity, proceed straight in;
   * otherwise show the kiosk. Registers a persistent insert watcher so a
   * freshly created Account (the authoritative source of truth) advances us in.
   */
  onConnected(conn: DbConnection, identityHex: string): void {
    atlas.identityHex = identityHex;

    // Reset on every (re)connect so the account-ready flow can fire again for
    // the new connection's account row — without this, a reconnect after the
    // first proceed() would stall forever with proceeded=true.
    this.proceeded = false;

    // Register the insert watcher exactly once (the SDK's onInsert returns void
    // so there is no unsubscribe; re-registering on every reconnect stacks
    // duplicate callbacks that fire for every subsequent connection).
    if (!this.insertWatcherBound) {
      this.insertWatcherBound = true;
      // The Account row is the source of truth — watch for it (initial resolve
      // on reconnect, or the row created by createAccount on first run).
      conn.db.account.onInsert((_ctx, row) => {
        if (conn.identity && row.identity.isEqual(conn.identity)) this.proceed(row);
      });
    }

    const existing = conn.db.account.identity.find(conn.identity!);
    if (existing) {
      this.proceed(existing);
    } else {
      this.show();
    }
  }

  private proceed(row: { username: string; displayName: string }): void {
    if (this.proceeded) return;
    this.proceeded = true;
    atlas.hasAccount = true;
    atlas.username = row.username;
    atlas.displayName = row.displayName;
    this.submitBtn.disabled = false;
    this.hide();
    bus.emit('atlas:account-ready', {
      identityHex: atlas.identityHex!,
      username: row.username,
      displayName: row.displayName,
    });
  }

  private show(): void {
    this.overlay.classList.add('show');
    this.usernameInput.focus();
  }

  private hide(): void {
    this.overlay.classList.remove('show');
  }

  private submit(): void {
    const conn = atlas.conn;
    if (!conn || this.proceeded) return;

    const username = this.usernameInput.value.trim();
    const displayName = this.displayInput.value.trim() || username;

    if (username.length < 3 || username.length > 20 || !/^[A-Za-z0-9_]+$/.test(username)) {
      this.errEl.textContent = 'Username must be 3–20 chars: letters, digits, underscore.';
      return;
    }

    this.submitBtn.disabled = true;
    this.errEl.textContent = 'Creating your passport…';

    // Success advances via the persistent account.onInsert watcher above.
    void conn.reducers.createAccount({ username, displayName }).catch((err: Error) => {
      this.submitBtn.disabled = false;
      this.errEl.textContent = err.message || 'Could not create account.';
    });
  }
}
